import { Zap } from 'lucide-react';
import { AddOnFeature } from '../AddOns/components/add-on-feature';
import classNames from 'classnames';

function AddOnBadge({ className, count }: { className?: string; count?: number; }) {
  return (
    <span
      className={classNames(
        "inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs w-fit text-[#FF4C00]",
        "border-[#FF4C00] bg-[#FFF0F1] text-destructive",
        className)}
    >
      <Zap className="text-[#FF4C00]" width={20} />
      Step {count}
    </span>
  )
}


export default function SchoolMarketing() {
    return (
        <div className="min-h-screen bg-white">
            {/* Header */}
            <header className="max-w-6xl mx-auto px-6 py-16 text-center sm:px-4 sm:py-8">
                <h1 className="text-5xl font-bold text-gray-900 mb-6 md:text-4xl sm:text-3xl">
                    SooMuch.Ai - India's First AI-Powered<br />School Marketing Platform
                </h1>
                <div className="max-w-4xl mx-auto text-gray-700 leading-relaxed space-y-4">
                    <p className="font-semibold text-base">
                        What if your school had a dedicated marketing agency — working 365 days a year — helping you generate up to ₹2.5 crores in admissions,
                        without spending a single rupee?
                    </p>
                    <div className="text-sm space-y-3 text-left bg-gray-50 p-6 rounded-lg leading-relaxed">
                        <p>SooMuch.Ai — <strong>India’s first and only AI-powered marketing platform</strong> built exclusively for Indian schools.</p>
                        <p>We offer <strong>over ₹5 lakhs worth of AI tools</strong> and services <strong>100% free for the first year</strong>. No staff required. No marketing budget needed. No digital agency.</p>
                        <p>Double admissions in just 3 years, reach <strong>up to 1 lakh+ new parents</strong>, and grow consistently — through intelligent, emotional, multilingual marketing.</p>
                        <p>Funded by <strong>Microsoft</strong>, In collaboration with <strong>Meta & Google</strong>, and aligned with <strong>PM Modi’s Bhashini mission</strong>, SooMuch.Ai brings world-class tools to your doorstep — in your language, under your school’s name.  </p>
                    </div>
                </div>
            </header>

            {/* Main Features */}
            <main className="max-w-6xl mx-auto px-6 py-12 space-y-24 md:space-y-16 sm:px-4">

                {/* Website Feature */}
                <AddOnFeature
                    badge={<AddOnBadge count={1} />}
                    cta=""
                    imageAlt="abc"
                    title="AI-Based Website for Your School"
                    imageQuery="images/new-landing-page/marketing1.svg"
                    description={
                        <div className="space-y-3">
                            <p><strong>Upload your photos</strong> — even blurry ones — and our AI enhances them, writes SEO-rich content, and creates your responsive website in under 2 minutes.</p>
                            <p><strong>Auto-connects</strong> to Facebook, Google, and Instagram, making your school instantly discoverable to parents — instantly. Reports showcases online presence that drives discoverability and credibility.</p>
                            <p><strong>AI-Based Website Testimonial Collection</strong></p>
                            <p>Our AI invites Pro-prompted for parents to leave testimonial, review that you wish your school to promote their testimonials, and enhances testimonials. On-clicking yes, one written-options allow one-click submission. Testimonials updated instantly on the website, enhancing credibility and trust.</p>
                        </div>
                    }
                />

                {/* Social Hub Feature */}
                <AddOnFeature
                    badge={<AddOnBadge count={2} />}
                    cta=""
                    imageAlt="abc"
                    reverse
                    title="AI-Based Social Hub"
                    imageQuery="images/new-landing-page/marketing2.svg"
                    description={
                        <div className="space-y-3">
                            <p><strong>365-Day Social Media Engine</strong> posts daily news, festival greetings, and birthday celebrations automatically.</p>
                            <p><strong>Auto-branded</strong> with your logo, name, hashtags, and school information.</p>
                            <p><strong>Parents</strong> share posts, boosting emotional reach and driving engagement — all on auto with zero effort and zero ad spend.</p>
                            <p><strong>Benefit:</strong> Consistent social engagement and increased school visibility.</p>
                        </div>
                    }
                />

                {/* Podcasts Feature */}
                <AddOnFeature
                    badge={<AddOnBadge count={3} />}
                    cta=""
                    imageAlt="abc"
                    title="AI-Based Inspire365 & Parenting Podcasts"
                    imageQuery="images/new-landing-page/marketing3.svg"
                    description={
                        <div className="space-y-3">
                            <p><strong>Students get daily inspiration</strong> content through stories of Indian legends.</p>
                            <p><strong>Parents receive</strong> video and voice podcasts addressing real issues like screen time, stress, and anxiety — in 10+ Indian languages, new topics consistently added.</p>
                            <p><strong>Delivered via Curriculum and WhatsApp:</strong> fostering deep emotional loyalty.</p>
                            <p><strong>Benefit:</strong> Strengthen parent-student-school relationship and builds long-term loyalty.</p>
                        </div>
                    }
                />

                {/* Virtual Counselor */}
                <AddOnFeature
                    badge={<AddOnBadge count={4} />}
                    cta=""
                    imageAlt="abc"
                    reverse
                    title="AI-Based 24/7 Virtual Admission Counselor"
                    imageQuery="images/new-landing-page/marketing4.svg"
                    description={
                        <div className="space-y-3">
                            <p><strong>AI counselor works non-stop</strong> — answers questions, shares brochures and your school's social links, books appointments, and collects WhatsApp leads.</p>
                            <p><strong>Available 10+ Indian languages,</strong> speaking holidays + 24/7/365 days a week.</p>
                            <p><strong>Never miss an inquiry,</strong> ensuring faster lead conversions.</p>
                            <p><strong>Benefit:</strong> Always-on admissions support that boosts conversion and reduces manual workload.</p>
                        </div>
                    }
                />

                {/* Communication Engine */}
                <AddOnFeature
                    badge={<AddOnBadge count={5} />}
                    cta=""
                    imageAlt="abc"
                    title="AI-Based Multilingual Communication Engine"
                    imageQuery="images/new-landing-page/marketing5.svg"
                    description={
                        <div className="space-y-3">
                            <p><strong>Sends</strong> attendance updates, exam notifications, fee reminders, and event invitations automatically — all via SMS, WhatsApp and SMS.</p>
                            <p><strong>Messages delivered</strong> in each parent's preferred language, translate and are engagement-ready.</p>
                            <p><strong>Benefit:</strong> Eliminates communication gaps and keeps parents fully informed.</p>
                        </div>
                    }
                />

                {/* Admin Automation */}
                <AddOnFeature
                    badge={<AddOnBadge count={6} />}
                    cta=""
                    imageAlt="abc"
                    reverse
                    title="AI-Based Admin Automation"
                    imageQuery="images/new-landing-page/marketing6.svg"
                    description={
                        <div className="space-y-3">
                            <p><strong>Saves 4,000+ admin hours annually.</strong></p>
                            <p><strong>Automates</strong> fee reminders, QR attendance, homework tracking, and general school management.</p>
                            <p><strong>Insight Engine collects</strong> multilingual feedback, generates engagement reports, and notifies parents when actions are needed.</p>
                            <p><strong>Benefit:</strong> Reduces workload, boosts trust, and lowers dropout rates.</p>
                        </div>
                    }
                />

                {/* Results Section */}
                <AddOnFeature
                    badge={<AddOnBadge count={7} />}
                    cta=""
                    imageAlt="abc"
                    title="AI-Based Results You Can Expect Over ₹5 lakh worth of tools free in Year 1."
                    imageQuery="images/new-landing-page/marketing7.svg"
                    description={
                        <div className="space-y-3">
                            <p><strong>Schools gain:</strong></p>
                            <p>Up to ₹2.5 crore in new revenue</p>
                            <p>Upto Double admissions in 3 years </p>
                            <p>Reach 1,00,000+ new parents — without paid ads</p>
                            <p><strong>Up to ₹2.5 crore in new revenue</strong></p>
                            <p>Less ₹1.5 lakhs investment in 5 years.</p>
                            <p><strong>SooMuch.Ai becomes</strong> your full-time marketing partner, admissions engine, parent app, and automation dashboard.</p>
                            <p><strong>Benefit:</strong> Measurable growth revenue, and scalable infrastructure without hiring.</p>
                        </div>
                    }
                />

            </main>
        </div>
    );
}